/**
 * The Stream Manager's activity feed, derived from runtime changes. Pure: a snapshot of what the
 * feed cares about, and the events that separate two snapshots.
 */
import type { StreamingSettings } from '../../../core/model';
import type { RuntimeState } from '../../../engine/api';
import type { HealthEvent, HealthIssue } from '../../../health/api';
import type { LinkStatus } from '../../../net/api';
import type { RecorderStatus } from '../../../output/api';
import { linkStatusOf } from '../../actions/outputs';
import type { ActivityGroup } from '../../uiStore';

export type ActivityKind =
  | 'live'
  | 'offline'
  | 'reconnecting'
  | 'error'
  | 'viewers'
  | 'guest-joined'
  | 'guest-left'
  | 'recording-started'
  | 'recording-stopped'
  | 'clip'
  | 'health';

export interface ActivityEvent {
  id: number;
  at: number;
  kind: ActivityKind;
  text: string;
}

/** An event before the store numbers it. */
export type ActivityDraft = Omit<ActivityEvent, 'id'>;

export interface ActivitySnapshot {
  linkStatus: LinkStatus;
  linkError: string | null;
  viewers: number;
  guests: readonly { id: string; name: string }[];
  recording: RecorderStatus;
  /** Health events as the runtime holds them: oldest first, bounded. */
  healthEvents: readonly HealthEvent[];
  healthIssues: readonly HealthIssue[];
}

/** Viewer counts announced when the audience grows past them. */
export const VIEWER_MILESTONES = [5, 10, 25, 50, 100, 250, 500, 1000] as const;

/** Viewers of the selected output: the swarm's count, connected P2P viewers, nothing known for WHIP. */
export function viewersOf(mode: StreamingSettings['mode'], runtime: Pick<RuntimeState, 'swarm' | 'broadcast'>): number | null {
  if (mode === 'swarm') return runtime.swarm.viewers;
  if (mode === 'p2p') return runtime.broadcast.peers.filter((peer) => peer.role === 'viewer').length;
  return null;
}

export function snapshotOf(runtime: RuntimeState, mode: StreamingSettings['mode']): ActivitySnapshot {
  const linkStatus = linkStatusOf(mode, runtime);
  const link = mode === 'whip' ? runtime.whip : mode === 'swarm' ? runtime.swarm : runtime.broadcast;
  return {
    linkStatus,
    linkError: link.error,
    viewers: viewersOf(mode, runtime) ?? 0,
    guests: runtime.guests.map((guest) => ({ id: guest.guestId, name: guest.name })),
    recording: runtime.recording.status,
    healthEvents: runtime.health.events,
    healthIssues: runtime.health.issues,
  };
}

const lastAt = (events: readonly HealthEvent[]): number => events.reduce((max, event) => Math.max(max, event.at), -Infinity);

/** Raised problems that are warnings or worse, and failed recoveries. */
function notableHealth(event: HealthEvent, issues: readonly HealthIssue[]): boolean {
  if (event.type === 'recovery-failed') return true;
  if (event.type !== 'issue-raised') return false;
  const issue = issues.find((i) => i.code === event.code && i.peerId === event.peerId);
  return issue ? issue.severity !== 'info' : event.code !== 'page-hidden';
}

const ON_AIR: readonly LinkStatus[] = ['live', 'reconnecting'];
const RECORDING_ACTIVE: readonly RecorderStatus[] = ['recording', 'paused', 'stopping'];

export function deriveActivity(prev: ActivitySnapshot, next: ActivitySnapshot, at: number): ActivityDraft[] {
  const out: ActivityDraft[] = [];
  const push = (kind: ActivityKind, text: string): void => void out.push({ at, kind, text });

  if (prev.linkStatus !== next.linkStatus) {
    if (next.linkStatus === 'live') push('live', prev.linkStatus === 'reconnecting' ? 'Reconnected' : 'Went live');
    else if (next.linkStatus === 'reconnecting') push('reconnecting', 'Reconnecting to the server…');
    else if (next.linkStatus === 'error') push('error', `Stream error: ${next.linkError ?? 'unknown error'}`);
    else if (next.linkStatus === 'idle' && ON_AIR.includes(prev.linkStatus)) push('offline', 'Stream ended');
  }

  if (next.viewers > prev.viewers) {
    if (prev.viewers === 0) push('viewers', 'First viewer arrived');
    const crossed = VIEWER_MILESTONES.filter((m) => prev.viewers < m && next.viewers >= m);
    const highest = crossed[crossed.length - 1];
    if (highest !== undefined) push('viewers', `${highest} viewers watching`);
  }

  const before = new Set(prev.guests.map((g) => g.id));
  const after = new Set(next.guests.map((g) => g.id));
  for (const guest of next.guests) if (!before.has(guest.id)) push('guest-joined', `Guest ${guest.name || 'Guest'} joined`);
  for (const guest of prev.guests) if (!after.has(guest.id)) push('guest-left', `Guest ${guest.name || 'Guest'} left`);

  if (next.recording !== prev.recording) {
    if (next.recording === 'recording' && !RECORDING_ACTIVE.includes(prev.recording)) push('recording-started', 'Recording started');
    else if (next.recording === 'idle' && RECORDING_ACTIVE.includes(prev.recording)) push('recording-stopped', 'Recording stopped');
  }

  if (next.healthEvents !== prev.healthEvents) {
    const seen = lastAt(prev.healthEvents);
    for (const event of next.healthEvents) {
      if (event.at > seen && notableHealth(event, next.healthIssues)) push('health', `Health: ${event.detail}`);
    }
  }

  return out;
}

/** The feed filter's groups, as the activity kinds fall into them. */
export const ACTIVITY_GROUP_OF: Record<ActivityKind, ActivityGroup> = {
  live: 'stream',
  offline: 'stream',
  reconnecting: 'stream',
  error: 'stream',
  viewers: 'viewers',
  'guest-joined': 'guests',
  'guest-left': 'guests',
  'recording-started': 'recording',
  'recording-stopped': 'recording',
  clip: 'recording',
  health: 'health',
};

export const ACTIVITY_GROUP_LABELS: Record<ActivityGroup, string> = {
  stream: 'Stream',
  viewers: 'Viewers',
  guests: 'Guests',
  recording: 'Recording',
  health: 'Health',
};

/** Kinds that sound the activity beep when it is on. */
export const ACTIVITY_SOUND_KINDS: ReadonlySet<ActivityKind> = new Set(['viewers', 'guest-joined', 'guest-left']);
